import React, { useEffect, useMemo, useState } from 'react';
import markUnread from 'hoc/markUnread';
import PatientAppointmentDetails from 'components/HealthRecords/Appointments/PatientAppointmentDetails';
import AppointmentUtils from 'services/AppointmentUtils';
import DefaultButton from 'components/Utils/NewDefaultButton';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import TopBar from 'scenes/HomePatient2/TopBar';
import { useNavStack } from 'careand-redux/slices/ui/hooks';
import { Stacks, Tabs } from 'careand-redux/slices/ui/navigation';
import { usePathParams } from 'components/Navigation/NavStack';
import Icon from 'components/Utils/NewIcon';
import './style.scss';
import CalendarUtils from 'services/CalendarUtils';
import { PatientImage, PhysicianImage } from 'components/Common/UserImage';
import StaffUtils from 'services/StaffUtils';
import DefaultPopup from 'components/Utils/DefaultPopup';
import RateAppointment from 'components/HealthRecords/Appointments/RateAppointment';
import UnreadBadge from 'components/Common/UnreadBadge';
import emptyAppointmentsImage from 'assets/img/empty-appointments.webp';
import VirtualizedList from 'components/Utils/VirtualizedList';
import moment from 'moment-timezone';
import PatientUtils from 'services/PatientUtils';
import NewAppointmentBadge from '../NewAppointmentBadge';

const MarkUnreadAppointmentDetails = markUnread(PatientAppointmentDetails, 'Appointment');

const PatientAppointmentList = () => {
  const nav = useNavStack(Stacks.Appointments);
  const activeTab = useAppSelector((state) => state.navigation.activeTab);

  const [ratingAppointment, setRatingAppointment] = useState(undefined);
  const activeUserId = useAppSelector((state) => state.authentication.activeUserId);
  const dispatch = useAppDispatch();
  const allAppointments = useAppSelector((state) => state.appointments.byId);

  const params = usePathParams();
  const userId = params.userId || activeUserId;
  const selectedId = params.id;

  const appointments = useAppSelector((state) => state.appointments.byId[userId]);

  useEffect(() => {
    dispatch(api.Appointments.getOwnAppointments({ userId, limit: 1000 }));
  }, [dispatch, userId]);

  const selectedItem = useMemo(() => {
    let result = undefined;
    Object.keys(allAppointments).forEach((patientId) => {
      let found = (allAppointments[patientId] || []).find(({ id }) => id === selectedId);
      result = result || found;
    });
    return result;
  }, [selectedId, allAppointments]);

  const upcomingAppointments = useMemo(
    () => AppointmentUtils.getUpcomingAppointments(appointments),
    [appointments],
  );

  const pastAppointments = useMemo(
    () => AppointmentUtils.getPastAppointments(appointments),
    [appointments],
  );

  function selectItem(appointment) {
    nav.push(`/${userId}/${appointment.id}`);
  }

  function onBooking() {
    nav.push('/booking/');
  }

  const ratingCutoff = moment().subtract(1, 'month').unix();

  function getAppointmentsContent(title, appointments) {
    if (appointments.length > 0) {
      return [
        <Title title={title} />,
        ...appointments.map((appointment) => {
          function onClick() {
            selectItem(appointment);
          }

          return (
            <NewAppointmentBadge
              key={appointment.id}
              appointment={appointment}
              onClick={onClick}
              onShowRating={
                appointment.date > ratingCutoff
                  ? (appointment) => setRatingAppointment(appointment)
                  : undefined
              }
            />
          );
        }),
      ];
    }
    return [];
  }

  const upcomingAppointmentsContent = getAppointmentsContent(
    'Upcoming Appointments',
    upcomingAppointments,
  );
  const pastAppointmentsContent = getAppointmentsContent('Past Appointments', pastAppointments);

  return (
    <div className="page patient-appointment-list">
      <TopBar
        title="Appointments"
        rightElement={
          activeTab === Tabs.Appointments ? (
            <Icon src="calendar-plus" height="20px" onClick={onBooking} />
          ) : null
        }
      />
      <DefaultPopup
        title="Rate Appointment"
        className="appointment-rating-popup"
        openHandler={[!!ratingAppointment, (value) => setRatingAppointment(undefined)]}
      >
        {ratingAppointment && (
          <RateAppointment
            appointment={ratingAppointment}
            onSuccess={() => setRatingAppointment(undefined)}
          />
        )}
      </DefaultPopup>
      {!appointments?.length && (
        <div className="page-content patient-appointment-list-content padded-box">
          <NoListContent
            text="You have not booked any appointments yet"
            image={emptyAppointmentsImage}
          >
            <DefaultButton
              name="Book Appointment"
              className="blue"
              icon="calendar-clock"
              onClick={onBooking}
            />
          </NoListContent>
        </div>
      )}
      {!selectedItem && appointments?.length && (
        <VirtualizedList
          className="page-content patient-appointment-list-content"
          items={[...upcomingAppointmentsContent, ...pastAppointmentsContent]}
          defaultHeight={141}
        />
      )}
      {!!selectedItem && (
        <div className="page-content patient-appointment-list-content padded-box">
          <MarkUnreadAppointmentDetails appointment={selectedItem} unreadItem={selectedItem} />
        </div>
      )}
    </div>
  );
};

/**
 * @param {any} prop
 */
export const NoListContent = ({ title, text = 'No appointments to show', image, children }) => (
  <div className="item-not-available">
    <div className="content">
      {title && <Title title={title} />}
      {image && <img className="image" src={image} alt={text} width={200} />}
      <span className="text">{text}</span>
      {children}
    </div>
  </div>
);

const Title = ({ title }) => <span className="small-title">{title}</span>;

export default PatientAppointmentList;
